import * as Switch from '@studio/design-system/switch';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import type { Proxy } from '#~/agent/index.js';
import { DataSTag, Sel } from '#~/stag/tag.js';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

// Derive a kebab-case slug from the protobuf-generated numeric
// DeviceRegistryCommandKey enum so the STag attribute reads like
// `data:control-room.right-tray.device-setting.zoom-compensation`.
// Same reverse-mapping pattern used by sections.tsx:108 and
// conditions.ts:82 (`DeviceRegistryCommandKey[key]`).
// Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
// stamping the wrapper here makes every DeviceSettingSwitch
// selectable by STag for the IR-034..IR-039 control-tray suite,
// replacing the prior visible-label `hasText` fallback.
const slugifyCmdKey = (key: DeviceRegistryCommandKey): string =>
  DeviceRegistryCommandKey[key]?.toLowerCase().replace(/_/g, '-') ?? 'unknown';

interface DeviceSettingSwitchProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  deviceId: Device['id'];
  disabled?: boolean;
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onReactiveMutation?: T_Maybe<(mutation: DeepPartial<Control>) => Promise<void>>;
  value: boolean;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingSwitch>` component. */
export const DeviceSettingSwitch = ({
  agent,
  className,
  cmdKey,
  deviceId,
  disabled,
  label,
  onMutation,
  onReactiveMutation,
  value,
  onControlClick,
  status
}: DeviceSettingSwitchProps): ReactElement => {
  const mutateSwitchValue = async (nextValue: boolean): Promise<void> => {
    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              key: cmdKey,
              toggle: nextValue,
              lastModified: Date.now()
            }
          }
        }
      }
    };

    // Use reactive mutation handler if available, otherwise fall back to legacy behavior
    if (onReactiveMutation) {
      await onReactiveMutation(control);
    } else if (onMutation) {
      onMutation(control);
    } else {
      await agent?.mutate({ control }, {});
    }

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.SWITCH,
      key: cmdKey,
      value: nextValue
        ? DeviceRegistryCommandFunctionType.ON
        : DeviceRegistryCommandFunctionType.OFF
    });
  };

  return (
    <Switch.Container
      className={className}
      label={label}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
      // Container's `...props` spread (design-system/src/switch/container.tsx:22)
      // forwards `data-stag` onto the wrapper `<div>` — no design-system
      // signature change needed.
      {...DataSTag(Sel.MainRoomRightTrayDeviceSetting, slugifyCmdKey(cmdKey))}
    >
      <Switch.Archetype checked={value} disabled={disabled} onCheckedChange={mutateSwitchValue} />
    </Switch.Container>
  );
};
